import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
} from "@mui/material";
import { GetTimeString } from "../utiles";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TimerIcon from "@mui/icons-material/Timer";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import "../pages/Tools.css";
import { useDispatch } from "react-redux";
import { GetTournamentList } from "../store/user/actions";

export const TournamentCard = ({
  id,
  is_started,
  end_time,
  winner1,
  winner2,
  is_entered,
  setId,
  is_open,
  details
}: any) => {
  const dispatch = useDispatch<any>();
  const [uTime, setUTime] = useState(0);
  const [status, setStatus] = useState("");
  const [is_RegistrationOpen, setIs_RegistrationOpen] = useState(false);
  const [is_RegistrationClosed, setIs_RegistrationClosed] = useState(false);
  const [result_available, setResultAvailable] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    const updateTime = () => {
      const currentUnixTime = Math.floor(Date.now() / 1000);
      const waitTime = end_time - currentUnixTime;

      setUTime(waitTime > 0 ? waitTime : 0);

      const registrationStarted = details?.reg_start ? details.reg_start <= currentUnixTime : false;
      const registrationEnded = details?.reg_end ? details.reg_end <= currentUnixTime : false;

      setIs_RegistrationOpen(registrationStarted);
      setIs_RegistrationClosed(registrationEnded);

      if (is_open) setStatus("active");
      else if (is_started) setStatus("running");
      else setStatus("finished");
    };

    updateTime();

    if (end_time > Math.floor(Date.now() / 1000)) {
      timer = setInterval(updateTime, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [end_time, details, is_open, is_started]);

  useEffect(() => {
    // Results are available if there are declared winners or match history
    const resultsDeclared =
      (details?.results && (details.results.winner1 || details.results.winner2)) ||
      (winner1 || winner2) ||
      (Array.isArray(details?.matches) && details.matches.length > 0);

    setResultAvailable(resultsDeclared);
  }, [details, winner1, winner2, id]);

  const onEnterTournament = () => {
    if (uTime > 0 && !is_entered) {
      setId(id);
    } else {
      // For viewing results, ensure we have fresh data
      if (result_available) {
        // Reload tournament data before showing results
        dispatch(GetTournamentList());
        // Small delay to ensure data is loaded
        setTimeout(() => setId(-id), 500);
      } else {
        setId(-id);
      }
    }
  };

  const formatNumber = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0";
    if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(2) + "B";
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(2) + "K";
    return num.toFixed(2);
  };

  return (
    <Card
      sx={{
        height: "100%",
        background: uTime > 0
            ? (is_entered
              ? "linear-gradient(135deg, #232526 0%, #414345 100%)" // Active and entered
              : "linear-gradient(135deg, #141e30 0%, #243b55 100%)") // Active and not entered
            : "linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)", // Completed
        border: uTime > 0
            ? (is_entered ? "4px solid #1976d2" : "4px solid #43cea2")
            : "4px solid #5c3c10", // Completed
        borderRadius: "18px",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(.4,2,.6,1)",
        position: "relative",
        boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.25)",
        "&:hover": {
          transform: "scale(1.04) translateY(-8px)",
          boxShadow: "0 20px 40px 0 rgba(0,0,0,0.55)",
        },
        maxWidth: "350px",
        mx: "auto",
        filter: !is_entered && uTime <= 0 && !result_available ? "grayscale(0.5) brightness(0.85)" : undefined,
        pointerEvents: !is_entered && uTime <= 0 && !result_available ? "none" : undefined,
        opacity: !is_entered && uTime <= 0 && !result_available ? 0.7 : 1,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.10) 0px, rgba(92, 60, 16, 0.10) 2px, transparent 2px, transparent 6px)",
          opacity: 0.12,
          zIndex: 0,
        }}
      />

      <CardContent
        sx={{
          p: 3,
          position: "relative",
          zIndex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Chip
            label={
              <span>
                <span role="img" aria-label="trophy">🏆</span>
                <span className="text-center" style={{ marginLeft: 8  }}>Tournament #{id}</span>
              </span>
            }
            sx={{
              bgcolor: "rgba(255,255,255,0.10)",
              color: "white",
              fontWeight: "bold",
              fontSize: "1.15rem",
              py: 2,
              border: "2px solid #5c3c10",
              letterSpacing: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
              textShadow: "0 1px 4px rgba(0,0,0,0.18)",
            }}
          />
        </Box>
        {/* ...rest of your code remains unchanged... */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
          <Chip
            icon={uTime > 0 ? <TimerIcon /> : <EmojiEventsIcon />}
            label={
              uTime > 0
          ? "Active"
          : result_available
            ? "Completed"
            : "Result Awaited"
            }
            color={
              uTime > 0
          ? "warning"
          : result_available
            ? "success"
            : "info"
            }
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              px: 1,
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          />
        </Box>
        {/* ...rest of your code remains unchanged... */}
        <Box sx={{ flexGrow: 1, mb: 3 }}>
          {uTime > 0 ? (
            <Box
              sx={{
                bgcolor: "rgba(0,0,0,0.32)",
                p: 2.5,
                borderRadius: "14px",
                textAlign: "center",
                boxShadow: "0 2px 12px rgba(255,173,70,0.13)",
                mb: 2,
                border: "1.5px solid #ffad46",
                maxWidth: 300,
                mx: "auto",
              }}
            >
              {/* Prize Pool */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 0.5,
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#ffad46",
                    color: "#232526",
                    px: 0.8,
                    py: 0.2,
                    borderRadius: "6px",
                    fontWeight: "bold",
                    fontSize: "0.95rem",
                    display: "flex",
                    alignItems: "center",
                    boxShadow: "0 1px 4px rgba(255,173,70,0.13)",
                  }}
                >
                  <span role="img" aria-label="prize" style={{ marginRight: 4, fontSize: "1rem" }}>💰</span>
                  Prize:
                  <span
                    style={{
                      color: "#232526",
                      fontWeight: 700,
                      marginLeft: 4,
                      fontSize: "0.95rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    {formatNumber(details?.total_fees || "0")}
                    <img
                      src="/images/token_antlers.png"
                      alt="ANTLERS"
                      style={{ width: 20, height: 20, verticalAlign: "middle", marginLeft: 4 }}
                    />
                  </span>
                </Box>
              </Box>
              {/* Player Count */}
              <span style={{ fontSize: "1rem", color: "white", fontWeight: "bold", marginBottom: "1rem" }}>Players: {details?.entries?.length || 0} / {details?.rules?.max_players || 0}</span>
              {/* Registration Status */}
              {is_RegistrationOpen && !is_RegistrationClosed ? (
                <>
                  <Typography
                    variant="body2"
                    color="#2eca6a"
                    fontWeight="bold"
                    sx={{ letterSpacing: 0.5, mb: 0.5 }}
                  >
                    <span role="img" aria-label="open">🟢</span> Registration OPEN
                  </Typography>
                  {details?.reg_end && (
                    <Typography
                      variant="body2"
                      color="white"
                      sx={{ mb: 1 }}
                    >
                      Registration ends in:{" "}
                      <span style={{ color: "#ffad46", fontWeight: "bold" }}>
                        {GetTimeString(details.reg_end - Math.floor(new Date().getTime() / 1000))}
                      </span>
                    </Typography>
                  )}
                </>
              ) : is_RegistrationClosed && !is_started ? (
                <>
                  <Typography
                    variant="body2"
                    color="#ffad46"
                    fontWeight="bold"
                    sx={{ letterSpacing: 0.5, mb: 0.5 }}
                  >
                    <span role="img" aria-label="closed">🔒</span> Registration CLOSED
                  </Typography>
                  {details?.start_time && (
                    <Typography
                      variant="body2"
                      color="white"
                      sx={{fontSize: "0.85rem" }}
                    >
                      Tournament starts in:{" "}
                      <span style={{ color: "#ffad46", fontWeight: "bold", fontSize: "0.95em" }}>
                        {GetTimeString(details.start_time - Math.floor(new Date().getTime() / 1000))}
                      </span>
                    </Typography>
                  )}
                </>
              ) : is_started ? (
                <>
                  <Typography
                    variant="body2"
                    color="#1976d2"
                    fontWeight="bold"
                    sx={{ letterSpacing: 0.5, mb: 0.5 }}
                  >
                    <span role="img" aria-label="running">🚩</span> Tournament STARTED
                  </Typography>
                </>
              ) : null}

              {is_started ? (
                <>
                  <Typography
                    variant="body1"
                    color="white"
                    gutterBottom
                    fontWeight="medium"
                    sx={{ letterSpacing: 0.5, mt: 1 }}
                  >
                    Tournament ends in:
                  </Typography>
                  <Typography
                    variant="h4"
                    color="#ffad46"
                    fontWeight="bold"
                    sx={{
                      textShadow: "1px 2px 6px rgba(0,0,0,0.5)",
                      letterSpacing: 1,
                      mb: 1,
                      fontFamily: "monospace",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {GetTimeString(uTime || 0)}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    color="white"
                    gutterBottom
                    fontWeight="medium"
                    sx={{ letterSpacing: 0.5}}
                  >
                    Tournament starts in:
                  </Typography>
                  <Typography
                    variant="h6"
                    color="#ffad46"
                    fontWeight="bold"
                    sx={{
                      textShadow: "1px 2px 6px rgba(0,0,0,0.5)",
                      letterSpacing: 1,
                      fontFamily: "monospace",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {GetTimeString(uTime || 0)}
                  </Typography>
                </>
              )}
              {is_entered && (
                <Chip
                  label="Entry Confirmed 🎟️"
                  size="small"
                  color="primary"
                  icon={<PersonIcon />}
                  sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    fontWeight: "bold",
                    letterSpacing: 0.5,
                    boxShadow: "0 1px 4px rgba(25,118,210,0.18)",
                  }}
                />
              )}
            </Box>
            ) : (
            <Box sx={{ maxWidth: 300, mx: "auto" }}>
              {/* Prize Pool */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#ffad46",
                    color: "#232526",
                    px: 0.8,
                    py: 0.2,
                    borderRadius: "6px",
                    fontWeight: "bold",
                    fontSize: "0.95rem",
                    display: "flex",
                    alignItems: "center",
                    boxShadow: "0 1px 4px rgba(255,173,70,0.13)",
                  }}
                >
                  <span role="img" aria-label="prize" style={{ marginRight: 4, fontSize: "1rem" }}>💰</span>
                  Pool:
                  <span
                    style={{
                      color: "#232526",
                      fontWeight: 700,
                      marginLeft: 4,
                      fontSize: "0.95rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    {formatNumber(details?.total_fees || "0")}
                    <img
                      src="/images/token_antlers.png"
                      alt="ANTLERS"
                      style={{ width: 20, height: 20, verticalAlign: "middle", marginLeft: 4 }}
                    />
                  </span>
                </Box>
              </Box>
              <Typography
                variant="body2"
                color="rgba(255,255,255,0.92)"
                gutterBottom
                align="center"
                sx={{ fontWeight: "bold", letterSpacing: 0.5, mb: 1 }}
              >
                <span role="img" aria-label="medal">🏅</span> Winners:
              </Typography>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    bgcolor: "rgba(255,215,0,0.13)",
                    borderRadius: "10px",
                    border: "1.5px solid rgba(255,215,0,0.32)",
                    boxShadow: "0 1px 6px rgba(255,215,0,0.10)",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "gold",
                      color: "#000",
                      width: 40,
                      height: 40,
                      mr: 1,
                      border: "2px solid white",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                    }}
                  >
                    1
                  </Avatar>
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="body2"
                      color="rgba(255,255,255,0.92)"
                      fontSize="0.85rem"
                      fontWeight="bold"
                    >
                      First Place
                    </Typography>
                    <Typography variant="body1" color="white" noWrap>
                      {winner1}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    bgcolor: "rgba(192,192,192,0.13)",
                    borderRadius: "10px",
                    border: "1.5px solid rgba(192,192,192,0.32)",
                    boxShadow: "0 1px 6px rgba(192,192,192,0.10)",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "silver",
                      color: "#000",
                      width: 40,
                      height: 40,
                      mr: 1,
                      border: "2px solid white",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                    }}
                  >
                    2
                  </Avatar>
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="body2"
                      color="rgba(255,255,255,0.92)"
                      fontSize="0.85rem"
                      fontWeight="bold"
                    >
                      Second Place
                    </Typography>
                    <Typography variant="body1" color="white" noWrap>
                      {winner2 != null ? winner2 : "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          )}
          {uTime > 0 ? (
            <Button
              variant="contained"
              fullWidth
              onClick={onEnterTournament}
              disabled={
                is_entered || is_RegistrationClosed || (details?.entries?.length || 0) >= (details?.rules?.max_players || 0)
              }
              sx={{
                bgcolor: is_entered
                  ? "#1976d2"
                  : is_RegistrationClosed
                    ? "rgba(255,255,255,0.18)"
                    : "#ffad46",
                color: "white",
                fontWeight: "bold",
                borderRadius: "10px",
                marginTop: ".4rem",
                fontSize: "1.1rem",
                py: 1.5,
                boxShadow: "0 2px 8px rgba(0,0,0,0.14)",
                letterSpacing: 1,
                "&:hover": {
                  bgcolor: is_entered
                    ? "#1565c0"
                    : is_RegistrationClosed
                      ? "rgba(255,255,255,0.18)"
                      : "#e69b39",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.20)",
                },
                "&.Mui-disabled": {
                  bgcolor: "rgba(255,255,255,0.18)",
                  color: "rgba(255,255,255,0.5)",
                },
                transition: "all 0.2s cubic-bezier(.4,2,.6,1)",
              }}
              endIcon={<TimerIcon />}
            >
              {is_entered
                ? "You're In! 🎉"
                : is_RegistrationClosed
                  ? "Registration Closed"
                  : "Enter Tournament"}
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={onEnterTournament}
              disabled={!result_available}
              sx={{
                bgcolor:
                  !result_available
                    ? "rgba(255,255,255,0.18)"
                    : "#2eca6a",
                color: "white",
                fontWeight: "bold",
                borderRadius: "10px",
                marginTop: "2rem",
                fontSize: "1.1rem",
                py: 1.5,
                boxShadow: "0 2px 8px rgba(0,0,0,0.14)",
                letterSpacing: 1,
                "&:hover": {
                  bgcolor:
                    !result_available
                      ? "rgba(255,255,255,0.18)"
                      : "#25a65a",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.20)",
                },
                "&.Mui-disabled": {
                  bgcolor: "rgba(255,255,255,0.18)",
                  color: "rgba(255,255,255,0.5)",
                },
                transition: "all 0.2s cubic-bezier(.4,2,.6,1)",
              }}
              endIcon={<EmojiEventsIcon />}
            >
              View Results
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};